import useClassNames from '@medflex/hooks/useClassNames';
import differenceInSeconds from 'date-fns/difference_in_seconds';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Duration from './Duration';
import useAudioPermission from './useAudioPermission';

const Recorder = ({ className, hidden, recording, onFinished, onStop, maxDuration, ...props }) => {
  const cn = useClassNames(
    () => [
      'h-full',
      'select-none',
      !hidden && 'flex',
      'items-center',
      'p-2',
      'transition',
      className,
    ],
    [className, hidden],
  );
  const { t } = useTranslation('components');
  const [recorder, setRecorder] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const { getUserAudio } = useAudioPermission();

  useEffect(() => {
    if (recording) {
      setStartDate(new Date());
      (async () => {
        try {
          const stream = await getUserAudio();
          const mediaRecorder = new MediaRecorder(stream);
          setRecorder(mediaRecorder);
          if (maxDuration) {
            setTimeout(() => {
              if (mediaRecorder && mediaRecorder.state === 'recording') {
                onStop();
              }
            }, maxDuration);
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(`Recorder#getUserMedia: ${e.message}`);
        }
      })();
    }
    return () => {
      setRecorder(null);
    };
  }, [recording]);

  useEffect(() => {
    if (recorder) {
      if (recording) {
        const chunks = [];
        setEndDate(null);

        const handleDataAvailable = e => {
          chunks.push(e.data);
        };

        const handleStop = () => {
          const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
          const audioUrl = window.URL.createObjectURL(blob);
          const now = new Date();
          setEndDate(now);
          onFinished({ url: audioUrl, duration: differenceInSeconds(now, startDate) });
        };

        recorder.addEventListener('dataavailable', handleDataAvailable);
        recorder.addEventListener('stop', handleStop);

        recorder.start();
      } else {
        recorder.stop();
        recorder.stream.getTracks()[0].stop();
      }
    }
  }, [recorder, recording]);

  return (
    <div data-component="VoiceMessage__Recorder" className={cn} hidden={hidden} {...props}>
      <div className="font-semibold">{t('VoiceMessage.Recorder.recording')}</div>
      {startDate && (
        <div className="ml-2 flex">
          <Duration startDate={startDate} endDate={endDate} />
        </div>
      )}
    </div>
  );
};

Recorder.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  hidden: PropTypes.bool,
  recording: PropTypes.bool,
  onFinished: PropTypes.func,
  onStop: PropTypes.func,
  maxDuration: PropTypes.number,
};

Recorder.defaultProps = {
  hidden: false,
  recording: false,
  onFinished: () => {},
  onStop: () => {},
  maxDuration: null,
};

export default Recorder;
